import { PriceDiff } from '@/common/ui/PriceDiff'
import { useListHoldings } from '@/trading/tradingQueries'
import { useFetchBalance } from '@/wallet/walletQueries'
import Money from '@/common/models/money'
import { useEffect, useState } from 'react'
import { useCampaignsWithHoldings } from './useCampaignsWithHoldings'
import { calculatePortfolioSummary } from './portfolioCalculator'
import { MegaphoneOff } from 'lucide-react'
import { Switch } from '@/common/ui/switch'
import { analytics } from '@/common/analytics/analytics'

function PortfolioSummaryView({
    totalValue,
    totalChange,
    totalChangePercent,
    changeSinceYesterday,
    changeSinceYesterdayPercent,
    hidePledged,
    setHidePledged,
    isLoading = false,
}: {
    totalValue?: number
    totalChange: number
    totalChangePercent: number
    changeSinceYesterday: number
    changeSinceYesterdayPercent: number
    hidePledged: boolean
    setHidePledged: (isChecked: boolean) => void
    isLoading?: boolean
}) {
    const handleToggle = (isChecked: boolean) => {
        analytics.trackWithDefaults('hide_campaign_share_toggled', {
            hideCampaignShare: isChecked,
        })
        localStorage.setItem('hidePledged', JSON.stringify(isChecked))
        setHidePledged(isChecked)
    }

    const changeSinceYesterdayValue = Money.of(Math.abs(changeSinceYesterday))
    const changeSinceYesterdayLabel = isFinite(changeSinceYesterdayPercent)
        ? `${changeSinceYesterdayValue} (${Math.abs(changeSinceYesterdayPercent || 0).toFixed(2)}%)`
        : `${changeSinceYesterdayValue}`

    const totalChangeValue = Money.of(Math.abs(totalChange))
    const totalChangeLabel = isFinite(totalChangePercent)
        ? `${totalChangeValue} (${Math.abs(totalChangePercent || 0).toFixed(2)}%)`
        : `${totalChangeValue}`

    return (
        <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-2 md:flex-row md:justify-between md:items-end">
            <div className="self-stretch flex flex-col md:flex-row md:items-center md:justify-start gap-4 md:gap-6 flex-1">
                {/* Top row: Portfolio label/value left, mobile switch right */}
                <div className="inline-flex justify-between items-center">
                    <div className="inline-flex flex-col justify-start items-start gap-1">
                        <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            Portfolio value
                        </div>
                        {isLoading ? (
                            <div className="h-7 flex items-center">
                                <div className="w-40">
                                    <div className="h-2 bg-stone-100 rounded" />
                                </div>
                            </div>
                        ) : (
                            <div className="justify-start text-black text-xl font-semibold font-['Inter'] leading-7">{`${Money.of(totalValue)}`}</div>
                        )}
                    </div>

                    <div className="w-11 h-6 relative md:hidden">
                        {/* mobile switch: visible on small screens, hidden on md+ */}
                        <div>
                            <Switch
                                thumbIcon={<MegaphoneOff size={14} />}
                                checked={hidePledged}
                                onCheckedChange={handleToggle}
                            />
                        </div>
                    </div>
                </div>

                {/* Stats stacked on mobile, inline on md+ */}
                <div className="self-stretch flex flex-col md:flex-row justify-start items-start gap-2 md:gap-6">
                    <div className="self-stretch inline-flex flex-row md:flex-col justify-start md:items-start items-end gap-2 md:gap-1">
                        <div className="order-1 md:order-2 h-7 inline-flex justify-start items-center">
                            {isLoading ? (
                                <div className="h-2 bg-stone-100 rounded w-28" />
                            ) : (
                                <PriceDiff className="font-semibold" label={totalChangeLabel} value={totalChange} />
                            )}
                        </div>
                        <div className="order-2 md:order-1 justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            Total
                        </div>
                    </div>

                    <div className="self-stretch inline-flex flex-row md:flex-col justify-start md:items-start items-end gap-2 md:gap-1">
                        <div className="order-1 md:order-2 h-7 inline-flex justify-start items-center">
                            {isLoading ? (
                                <div className="h-2 bg-stone-100 rounded w-28" />
                            ) : (
                                <PriceDiff
                                    className="font-semibold"
                                    label={changeSinceYesterdayLabel}
                                    value={changeSinceYesterday}
                                />
                            )}
                        </div>
                        <div className="order-2 md:order-1 justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            Day
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop-only right-side control */}
            <div className="hidden md:flex justify-start items-center gap-2">
                <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                    Exclude campaign shares
                </div>
                <Switch
                    className="align-middle h-6"
                    checked={hidePledged}
                    onCheckedChange={handleToggle}
                    thumbIcon={<MegaphoneOff size={14} />}
                />
            </div>
        </div>
    )
}

interface PortfolioValueProps {
    accountId: string | null
    hidePledged: boolean
    setHidePledged: (isChecked: boolean) => void
}

export function PortfolioSummary({ accountId, hidePledged, setHidePledged }: PortfolioValueProps) {
    const { data: holdingsData, isLoading: isLoadingHoldings } = useListHoldings(accountId)
    const { isLoading: isLoadingBalance } = useFetchBalance(accountId)
    const { campaignsWithHoldings } = useCampaignsWithHoldings()
    const [totalChange, setTotalChange] = useState(0)
    const [totalChangePercent, setTotalChangePercent] = useState(0)
    const [totalHoldingsValue, setTotalHoldingsValue] = useState(0)
    const [changeSinceYesterday, setChangeSinceYesterday] = useState(0)
    const [changeSinceYesterdayPercent, setChangeSinceYesterdayPercent] = useState(0)

    useEffect(() => {
        const pledgedSecurityCodes =
            campaignsWithHoldings?.flatMap((cp) =>
                Object.keys(cp.pledges ?? {}).filter((key) => cp.pledges[key] > 0),
            ) ?? []

        const calculations = calculatePortfolioSummary({
            holdings: holdingsData?.holdings || [],
            pledgedSecurityCodes,
            hidePledged,
        })

        setTotalHoldingsValue(calculations.totalHoldingsValue)
        setTotalChange(calculations.totalChange)
        setTotalChangePercent(calculations.totalChangePercent)
        setChangeSinceYesterday(calculations.changeSinceYesterday)
        setChangeSinceYesterdayPercent(calculations.changeSinceYesterdayPercent)
    }, [hidePledged, holdingsData?.holdings, campaignsWithHoldings])

    const isLoading = Boolean(isLoadingBalance || isLoadingHoldings)

    return (
        <PortfolioSummaryView
            isLoading={isLoading}
            totalChangePercent={Math.round(totalChangePercent * 100) / 100}
            changeSinceYesterdayPercent={Math.round(changeSinceYesterdayPercent * 100) / 100}
            changeSinceYesterday={Math.round(changeSinceYesterday * 100) / 100}
            totalChange={Math.round(totalChange * 100) / 100}
            totalValue={totalHoldingsValue}
            hidePledged={hidePledged}
            setHidePledged={setHidePledged}
        />
    )
}
